import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Form, Table, Button } from "react-bootstrap";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import moment from "moment";
import { isEmpty } from "lodash";

import MyAccountFilter from "../MyAccount/MyAccountFilter";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { levelMap, toFixedWithoutRounding } from "../../utils/constants";

const FMT = "YYYY-MM-DD HH:mm:ss";

const plSpan = (value) =>
  Math.sign(value) === -1 ? (
    <span className="text-danger">({toFixedWithoutRounding(value)})</span>
  ) : (
    <span>{toFixedWithoutRounding(value)}</span>
  );

const DownlineRow = ({ item, depth, filter }) => {
  const playerPL = Number(item?.stake?.amount || 0);
  const commission = Number(item?.stake?.commission || 0);
  const isUser = item?.userType === "user";

  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (loaded) return;

    const { status, data: response_users } = await apiGet(apiPath.reportParlayDownline, {
      page: 1,
      limit: filter.limit,
      fromPeriod: filter.fromPeriod,
      toPeriod: filter.toPeriod,
      filterByDay: filter.filterByDay,
      created_by: item?._id,
      userType: item?.userType,
    });
    if (status === 200 && response_users.success) {
      setChildren(response_users.results?.data || []);
    }
    setLoaded(true);
  };

  return (
    <>
      <tr className={depth === 1 ? "" : `expand expand_${depth}`}>
        <td className="text-start" style={{ paddingLeft: `${(depth - 1) * 20 + 10}px` }}>
          {!isUser &&
            (open ? (
              <AiFillMinusCircle
                onClick={toggle}
                size={16}
                style={{ marginRight: "5px", cursor: "pointer" }}
                color="rgba(59, 130, 246, 1)"
              />
            ) : (
              <AiFillPlusCircle
                onClick={toggle}
                size={16}
                style={{ marginRight: "5px", cursor: "pointer" }}
                color="rgba(59, 130, 246, 1)"
              />
            ))}
          <span className={`Bdlevel ${levelMap[item?.userType]}`} />
          {item?.username}
        </td>
        <td>{plSpan(playerPL)}</td>
        <td>{plSpan(playerPL)}</td>
        <td>
          <span>{toFixedWithoutRounding(commission)}</span>
        </td>
        <td>{plSpan(-playerPL)}</td>
      </tr>

      {open &&
        children.map((child, i) => (
          <DownlineRow key={child?._id || `${depth}-${i}`} item={child} depth={depth + 1} filter={filter} />
        ))}
    </>
  );
};

const ReportByParlayDownline = () => {
  const tableRef = useRef();

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    fromPeriod: moment().subtract(1, "day").format(FMT),
    toPeriod: moment().format(FMT),
    filterByDay: "",
  });

  const [data, setData] = useState([]);
  const [sumData, setSumData] = useState({});

  const getData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(apiPath.reportParlayDownline, activeFilter);
    if (status === 200 && response_users.success) {
      setData(response_users.results?.data || []);
      setSumData(response_users.results?.sumData || {});
    }
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page]);

  const applyPreset = (preset) => {
    const nextFilter = { ...filter, page: 1, filterByDay: preset };
    if (preset === "today") {
      nextFilter.fromPeriod = moment().startOf("day").format(FMT);
      nextFilter.toPeriod = moment().endOf("day").format(FMT);
    } else if (preset === "yesterday") {
      nextFilter.fromPeriod = moment().subtract(1, "day").startOf("day").format(FMT);
      nextFilter.toPeriod = moment().subtract(1, "day").endOf("day").format(FMT);
    }
    setFilter(nextFilter);
    getData(nextFilter);
  };

  const handleFromChange = (e) => {
    if (!e.target.value) return;
    setFilter((prev) => ({
      ...prev,
      filterByDay: "",
      fromPeriod: moment(e.target.value).startOf("day").format(FMT),
    }));
  };

  const handleToChange = (e) => {
    if (!e.target.value) return;
    setFilter((prev) => ({
      ...prev,
      filterByDay: "",
      toPeriod: moment(e.target.value).endOf("day").format(FMT),
    }));
  };

  const handleLimitChange = (e) =>
    setFilter((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }));

  const totalRecords = data?.length || 0;

  return (
    <div>
      <div className="user_data_div p-1">
        <section className="main-inner-outer py-3">
          <Container fluid>
            <div className="inner-wrapper">
              <div className="common-container">
                <Form onSubmit={(e) => e.preventDefault()}>
                  <div className="bet_status border-0 mb-0">
                    <div className="d-flex align-items-center flex-wrap mb-2">
                      <strong className="me-auto">Profit/Loss Report by parlay Downline</strong>
                      <span className="me-3">
                        Total Records: <strong>{totalRecords}</strong>
                      </span>
                      <MyAccountFilter type={"new"} tableRef={tableRef} fileName="report-parlay-downline" />
                    </div>

                    <Row>
                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">From</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              max={moment().format("YYYY-MM-DD")}
                              value={moment(filter.fromPeriod).format("YYYY-MM-DD")}
                              onChange={handleFromChange}
                            />
                            <Form.Control className="small_form_control" type="text" placeholder="00:00" disabled />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">To</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              min={moment(filter.fromPeriod).format("YYYY-MM-DD")}
                              max={moment().format("YYYY-MM-DD")}
                              value={moment(filter.toPeriod).format("YYYY-MM-DD")}
                              onChange={handleToChange}
                            />
                            <Form.Control className="small_form_control" type="text" placeholder="23:59" disabled />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">Last:</Form.Label>
                          <Form.Select className="small_select" value={filter.limit} onChange={handleLimitChange}>
                            <option value={100}>100 Txn</option>
                            <option value={50}>50 Txn</option>
                            <option value={25}>25 Txn</option>
                            <option value={10}>10 Txn</option>
                          </Form.Select>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="history-btn mb-3">
                          <ul className="list-unstyled mb-0">
                            <li>
                              <Button className="me-0 theme_light_btn" onClick={() => applyPreset("today")}>
                                Just For Today
                              </Button>
                            </li>
                            <li>
                              <Button className="me-0 theme_light_btn" onClick={() => applyPreset("yesterday")}>
                                From Yesterday
                              </Button>
                            </li>
                            <li>
                              <Button className="theme_light_btn theme_dark_btn" onClick={() => getData(filter)}>
                                Get P &amp; L
                              </Button>
                            </li>
                          </ul>
                        </div>
                      </Col>
                    </Row>

                    <div id="noReportMessage" className="mb-0">
                      <p className="p-0 m-0">
                        Bet List enables you to review the bets you have placed. <br />
                        Specify the time period during which your bets were placed, the type of markets on which
                        the bets were placed, and the sport.
                      </p>
                      <p className="p-0 m-0">Bet List is available online for the past 14 days.</p>
                    </div>
                  </div>
                </Form>

                <div className="account-table account-tabel-style batting-table">
                  <div className="responsive">
                    <Table ref={tableRef}>
                      <thead>
                        <tr>
                          <th scope="col">UID</th>
                          <th scope="col">Player P/L</th>
                          <th scope="col">Downline P/L</th>
                          <th scope="col">Agent Comm.</th>
                          <th scope="col">Upline P/L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!isEmpty(data) ? (
                          data.map((item, index) => (
                            <DownlineRow key={item?._id || index} item={item} depth={1} filter={filter} />
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5}>
                              <span>You have no bets in this time period.</span>
                            </td>
                          </tr>
                        )}

                        {!isEmpty(data) && (
                          <tr className="total">
                            <td className="text-start">
                              <strong>Total</strong>
                            </td>
                            <td>{plSpan(sumData?.PlayerPL || 0)}</td>
                            <td>
                              <span className={Math.sign(sumData?.PlayerPL) === -1 ? "text-danger" : ""}>
                                {toFixedWithoutRounding(sumData?.PlayerPL || 0)}
                              </span>
                            </td>
                            <td>
                              <span>{toFixedWithoutRounding(sumData?.commission || 0)}</span>
                            </td>
                            <td>
                              <span>{toFixedWithoutRounding(-(sumData?.PlayerPL || 0))}</span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </div>
    </div>
  );
};

export default ReportByParlayDownline;